// The visitor's cookie choice in the player app (statistics accepted or declined). Asked only when statistics
// (Umami) are switched on in the platform settings; kept in the browser for 12 months with the text version.
// Same rules as the customer portal (Breefer-tenant-web/src/lib/cookieChoice.ts).

export const COOKIE_TEXT_VERSION = "2026-09-22";
const KEY = "breeferCookieChoice";
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const COOKIE_CHOICE_EVENT = "breefer:cookie-choice";

export type CookieChoice = "accepted" | "declined";

export const getCookieChoice = (): CookieChoice | null => {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!stored || stored.version !== COOKIE_TEXT_VERSION || Date.now() - stored.at > MAX_AGE_MS) return null;
    return stored.choice === "accepted" ? "accepted" : "declined";
  } catch {
    return null;
  }
};

export const setCookieChoice = (choice: CookieChoice) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ choice, version: COOKIE_TEXT_VERSION, at: Date.now() }));
  } catch {
    // private mode: the choice lasts for this visit
  }
  window.dispatchEvent(new Event(COOKIE_CHOICE_EVENT));
  fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/privacy/cookie-choice`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ app: "player", choice, version: COOKIE_TEXT_VERSION }),
  }).catch(() => undefined);
};

export const resetCookieChoice = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(COOKIE_CHOICE_EVENT));
};

let analyticsConfig: Promise<{ provider: string; url?: string; playerWebsiteId?: string } | null> | null = null;
export const getAnalyticsConfig = () => {
  if (!analyticsConfig) {
    analyticsConfig = fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/public-config`)
      .then((res) => res.json())
      .then((config) => config?.analytics || null)
      .catch(() =>
        process.env.REACT_APP_UMAMI_URL
          ? { provider: "umami", url: process.env.REACT_APP_UMAMI_URL, playerWebsiteId: process.env.REACT_APP_UMAMI_WEBSITE_ID }
          : null
      );
  }
  return analyticsConfig;
};

/** Umami is loaded only after the visitor accepted statistics */
export const startAnalytics = () => {
  const load = () => {
    if (getCookieChoice() !== "accepted") return;
    getAnalyticsConfig().then((config) => {
      if (config?.provider !== "umami" || !config.url || !config.playerWebsiteId || document.querySelector("script[data-website-id]")) return;
      const script = document.createElement("script");
      script.defer = true;
      script.src = `${config.url.replace(/\/+$/, "")}/script.js`;
      script.dataset.websiteId = config.playerWebsiteId;
      script.dataset.doNotTrack = "true";
      document.head.appendChild(script);
    });
  };
  load();
  window.addEventListener(COOKIE_CHOICE_EVENT, load);
};
